import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetStruttura,
  useCreateStruttura,
  useUpdateStruttura,
  useGetHostProfiles,
  useGetCollaboratori,
  syncHostStruttura,
} from "@/lib/supabaseQueries";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, X, MapPin, CheckCircle, AlertCircle } from "lucide-react";

const TIPI = ["Appartamento", "Villa", "Casa", "Stanza", "B&B", "Hotel", "Altro"];
const VISIBILITA = ["Attiva", "Non attiva", "Manutenzione"];
const PAESI = ["Italia", "Francia", "Spagna", "Germania", "Portogallo", "Grecia", "Altro"];
const REGIONI = [
  "Abruzzo","Basilicata","Calabria","Campania","Emilia-Romagna","Friuli-Venezia Giulia",
  "Lazio","Liguria","Lombardia","Marche","Molise","Piemonte","Puglia","Sardegna","Sicilia",
  "Toscana","Trentino-Alto Adige","Umbria","Valle d'Aosta","Veneto",
];
const PARCHEGGIO = ["Nessuno", "Privato", "Condominiale", "Pubblico"];
const TABS = ["Struttura", "Foto & Media", "Servizi", "Team", "Info private", "Da sapere", "Posizione"];
const ACCENT = "#c87045";

const SERVIZI_CATEGORIE = [
  {
    nome: "Cucina",
    items: [
      { id: "macchinetta_caffe", label: "Macchinetta Caffè", emoji: "☕" },
      { id: "bollitore", label: "Bollitore", emoji: "🫖" },
      { id: "tostapane", label: "Tostapane", emoji: "🍞" },
      { id: "fornelli", label: "Fornelli", emoji: "🔥" },
      { id: "forno", label: "Forno", emoji: "🫕" },
      { id: "frigorifero", label: "Frigorifero", emoji: "🧊" },
      { id: "piatti_bicchieri", label: "Piatti e bicchieri", emoji: "🍽️" },
      { id: "lavastoviglie", label: "Lavastoviglie", emoji: "🫧" },
      { id: "microonde", label: "Microonde", emoji: "📦" },
    ],
  },
  {
    nome: "Camere da letto",
    items: [
      { id: "letti_confortevoli", label: "Letti confortevoli", emoji: "🛏️" },
      { id: "letto_matrimoniale", label: "Letto matrimoniale", emoji: "🛏️" },
      { id: "letto_singolo", label: "Letto singolo", emoji: "🛏️" },
      { id: "biancheria_fresca", label: "Biancheria fresca", emoji: "🌿" },
      { id: "armadio", label: "Armadio", emoji: "🚪" },
      { id: "cuscini_extra", label: "Cuscini extra", emoji: "💤" },
    ],
  },
  {
    nome: "Bagni",
    items: [
      { id: "doccia", label: "Doccia", emoji: "🚿" },
      { id: "vasca", label: "Vasca", emoji: "🛁" },
      { id: "asciugamani", label: "Asciugamani", emoji: "🧻" },
      { id: "prodotti_igiene", label: "Prodotti igiene", emoji: "🧴" },
      { id: "asciugacapelli", label: "Asciugacapelli", emoji: "💨" },
      { id: "lavatrice", label: "Lavatrice", emoji: "🌀" },
    ],
  },
  {
    nome: "Connettività",
    items: [
      { id: "wifi", label: "Wi-Fi", emoji: "📶" },
      { id: "smart_tv", label: "Smart TV", emoji: "📺" },
      { id: "netflix", label: "Netflix", emoji: "🎬" },
      { id: "prese_usb", label: "Prese USB", emoji: "🔌" },
    ],
  },
  {
    nome: "Comfort",
    items: [
      { id: "aria_condizionata", label: "Aria condizionata", emoji: "❄️" },
      { id: "riscaldamento", label: "Riscaldamento", emoji: "🌡️" },
      { id: "ventilatore", label: "Ventilatore", emoji: "🌬️" },
      { id: "ferro_stiro", label: "Ferro da stiro", emoji: "👔" },
    ],
  },
  {
    nome: "Outdoor & Extra",
    items: [
      { id: "balcone", label: "Balcone", emoji: "🌇" },
      { id: "terrazza", label: "Terrazza", emoji: "☀️" },
      { id: "piscina", label: "Piscina", emoji: "🏊" },
      { id: "giardino", label: "Giardino", emoji: "🌳" },
      { id: "parcheggio_privato", label: "Parcheggio privato", emoji: "🅿️" },
      { id: "ascensore", label: "Ascensore", emoji: "🔼" },
    ],
  },
];

const empty = {
  nome: "", slogan: "", tipo: "Appartamento", paese: "Italia", visibilita: "Attiva",
  regione: "", citta: "", indirizzo: "", piano: "", num_camere: 1, ospiti_max: 2,
  superficie: 0, parcheggio: "Nessuno", link_prenotazione: "", descrizione: "",
  telefono: "", cin: "", pet_friendly: false, host_assegnato: "", host_id: "", stato: "Attiva",
  foto_principale: "", logo: "",
  servizi: "[]",
  istruzioni_checkin: "", istruzioni_checkout: "", regole_casa: "", dotazioni: "",
  info_private_extra: "[]",
  cose_da_fare: "", dove_mangiare: "", trasporti_zona: "", parcheggio_note: "",
  da_sapere_extra: "[]",
  descrizione_posizione: "", latitudine: "", longitudine: "", mappa_embed: "",
};

type FormData = typeof empty;

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-3 rounded-lg border border-border bg-card text-base focus:outline-none focus:border-primary/50";
const textareaCls = `${inputCls} resize-none`;

function Input({ value, onChange, type = "text", placeholder }: { value: any; onChange: (v: any) => void; type?: string; placeholder?: string }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder || ""}
      onChange={e => onChange(type === "number" ? (e.target.value ? Number(e.target.value) : 0) : e.target.value)}
      className={inputCls}
      style={{ fontSize: "16px" }}
    />
  );
}

function Textarea({ value, onChange, rows = 4, placeholder }: { value: any; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <textarea
      value={value ?? ""}
      placeholder={placeholder || ""}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      className={textareaCls}
      style={{ fontSize: "16px" }}
    />
  );
}

function Select({ value, onChange, options }: { value: any; onChange: (v: string) => void; options: string[] }) {
  return (
    <select
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      className={inputCls + " appearance-none"}
      style={{ fontSize: "16px" }}
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// ── Tab 0: Struttura ──────────────────────────────────────────────────────────
function TabStruttura({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5">
        <Field label="Nome struttura" required>
          <Input value={form.nome} onChange={v => set("nome", v)} placeholder="Nome della struttura" />
        </Field>
        <Field label="Slogan">
          <Input value={form.slogan} onChange={v => set("slogan", v)} placeholder="Slogan della struttura" />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Paese">
          <Select value={form.paese} onChange={v => set("paese", v)} options={PAESI} />
        </Field>
        <Field label="Tipo struttura">
          <Select value={form.tipo} onChange={v => set("tipo", v)} options={TIPI} />
        </Field>
        <Field label="Visibilità">
          <Select value={form.visibilita} onChange={v => set("visibilita", v)} options={VISIBILITA} />
        </Field>
        <Field label="Regione">
          <Select value={form.regione || ""} onChange={v => set("regione", v)} options={["", ...REGIONI]} />
        </Field>
        <Field label="Città" required>
          <Input value={form.citta} onChange={v => set("citta", v)} placeholder="Inserisci la città" />
        </Field>
        <Field label="Indirizzo">
          <Input value={form.indirizzo} onChange={v => set("indirizzo", v)} placeholder="Inserisci l'indirizzo completo" />
        </Field>
        <Field label="Piano">
          <Input value={form.piano} onChange={v => set("piano", v)} placeholder="Es: Piano terra, 1° piano, 2° piano" />
        </Field>
        <Field label="Camere">
          <Input type="number" value={form.num_camere} onChange={v => set("num_camere", v)} />
        </Field>
        <Field label="Ospiti max">
          <Input type="number" value={form.ospiti_max} onChange={v => set("ospiti_max", v)} />
        </Field>
        <Field label="Superficie (mq)">
          <Input type="number" value={form.superficie} onChange={v => set("superficie", v)} />
        </Field>
        <Field label="Parcheggio">
          <Select value={form.parcheggio || "Nessuno"} onChange={v => set("parcheggio", v)} options={PARCHEGGIO} />
        </Field>
      </div>
      <Field label="Link prenotazione (Channel)">
        <Input value={form.link_prenotazione} onChange={v => set("link_prenotazione", v)} placeholder="Inserisci link prenotazione esterno" />
      </Field>
      <Field label="Descrizione">
        <Textarea value={form.descrizione} onChange={v => set("descrizione", v)} rows={4} placeholder="Inserisci una descrizione dettagliata della struttura" />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Telefono">
          <Input value={form.telefono} onChange={v => set("telefono", v)} placeholder="Inserisci numero di telefono" />
        </Field>
        <Field label="CIN (Codice Nazionale)">
          <Input value={form.cin} onChange={v => set("cin", v)} placeholder="Inserisci codice identificativo nazionale" />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => set("pet_friendly", !form.pet_friendly)}
          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
          style={{ backgroundColor: form.pet_friendly ? ACCENT : "#e8e3dc" }}
        >
          <span
            className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
            style={{ transform: form.pet_friendly ? "translateX(22px)" : "translateX(2px)" }}
          />
        </button>
        <span className="text-sm text-foreground">Pet friendly</span>
      </div>
    </div>
  );
}

// ── Image Upload Zone ─────────────────────────────────────────────────────────
function ImageUploadZone({
  value,
  onChange,
  label,
  hint,
  aspect = "wide",
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  hint?: string;
  aspect?: "wide" | "square";
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = aspect === "square" ? 400 : 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
      onChange(canvas.toDataURL("image/jpeg", 0.78));
    };
    img.src = url;
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const heightCls = aspect === "square" ? "h-36 w-36" : "h-52 w-full";

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      <div
        className={`relative ${heightCls} rounded-2xl border-2 transition-all cursor-pointer overflow-hidden`}
        style={{
          borderColor: dragging ? ACCENT : "#e8e3dc",
          borderStyle: value ? "solid" : "dashed",
          backgroundColor: value ? "transparent" : dragging ? "#fdf6f1" : "#faf8f5",
        }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {value ? (
          <>
            <img
              src={value}
              alt={label}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="text-white text-xs font-medium bg-black/50 px-3 py-1.5 rounded-full">
                Cambia immagine
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-4 text-center">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#f0ece8" }}
            >
              <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {dragging ? "Rilascia qui" : "Clicca o trascina"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {hint ?? "PNG, JPG, WEBP — max 5 MB"}
              </p>
            </div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
      </div>
    </div>
  );
}

// ── Tab 1: Foto & Media ───────────────────────────────────────────────────────
function TabFoto({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-8">
      <ImageUploadZone
        value={form.foto_principale}
        onChange={v => set("foto_principale", v)}
        label="Foto principale"
        hint="Immagine di copertina della struttura — PNG, JPG, WEBP"
        aspect="wide"
      />

      <div className="flex items-start gap-6">
        <ImageUploadZone
          value={form.logo}
          onChange={v => set("logo", v)}
          label="Logo struttura"
          hint="Logo quadrato — PNG con sfondo trasparente consigliato"
          aspect="square"
        />
        {form.logo && (
          <div className="flex-1 pt-7">
            <p className="text-xs text-muted-foreground mb-3">Anteprima logo</p>
            <div className="flex items-center gap-3 p-4 bg-muted/40 rounded-xl">
              <img src={form.logo} alt="Logo" className="w-10 h-10 object-contain rounded-lg" />
              <div>
                <p className="text-sm font-semibold text-foreground">{form.nome || "Nome struttura"}</p>
                <p className="text-xs text-muted-foreground">{form.citta || "Città"}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab 2: Servizi ────────────────────────────────────────────────────────────
function TabServizi({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  let selected: string[] = [];
  try { selected = JSON.parse(form.servizi || "[]"); } catch { selected = []; }

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id];
    set("servizi", JSON.stringify(next));
  };

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-6">Seleziona i servizi disponibili nella struttura.</p>
      {SERVIZI_CATEGORIE.map(cat => (
        <div key={cat.nome} className="mb-8">
          <h3 className="font-semibold text-foreground mb-3">{cat.nome}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {cat.items.map(item => {
              const checked = selected.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggle(item.id)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all text-left"
                  style={checked ? {
                    backgroundColor: "#f5ede5",
                    borderColor: "#c87045",
                    color: "#1a1814",
                  } : {
                    backgroundColor: "white",
                    borderColor: "#e8e3dc",
                    color: "#6b6b6b",
                  }}
                >
                  <div
                    className="flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center"
                    style={checked
                      ? { backgroundColor: ACCENT, borderColor: ACCENT }
                      : { borderColor: "#d0c8be" }
                    }
                  >
                    {checked && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
                        <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-base leading-none">{item.emoji}</span>
                  <span className="leading-tight">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Tab 3: Team ───────────────────────────────────────────────────────────────
function TabTeam({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  const { data: hostProfiles = [] } = useGetHostProfiles();
  const { data: allCollaboratori = [] } = useGetCollaboratori();

  let collabIds: number[] = [];
  try { collabIds = JSON.parse(form.info_private_extra || "[]").filter((x: any) => typeof x === "number"); } catch { collabIds = []; }

  const toggleCollab = (id: number, nome: string) => {
    if (collabIds.includes(id)) {
      set("info_private_extra", JSON.stringify(collabIds.filter(x => x !== id)));
    } else {
      set("info_private_extra", JSON.stringify([...collabIds, id]));
    }
  };

  return (
    <div className="space-y-6">
      {/* Host */}
      <div className="border border-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-base">👤</span>
          <h3 className="font-semibold text-foreground">Host responsabile</h3>
        </div>
        {(hostProfiles as any[]).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun host disponibile. Creane uno nella sezione Team.</p>
        ) : (
          <div className="relative">
            <select
              value={form.host_id}
              onChange={e => {
                const h = (hostProfiles as any[]).find((x: any) => x.id === e.target.value);
                set("host_id", e.target.value);
                set("host_assegnato", h?.nome_display ?? "");
              }}
              className={inputCls + " appearance-none pr-8"}
            >
              <option value="">— Seleziona host —</option>
              {(hostProfiles as any[]).map((h: any) => (
                <option key={h.id} value={h.id}>{h.nome_display} — {h.ruolo || "Host"}</option>
              ))}
            </select>
            <X className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          </div>
        )}
        {form.host_assegnato && (
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <span>ℹ️</span> Responsabile operazioni della struttura
          </p>
        )}
      </div>

      {/* Collaboratori */}
      <div className="border border-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-base">👥</span>
          <h3 className="font-semibold text-foreground">Collaboratori</h3>
        </div>
        {(allCollaboratori as any[]).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun collaboratore disponibile. Creane uno nella sezione Team.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(allCollaboratori as any[]).map((c: any) => {
              const checked = collabIds.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggleCollab(c.id, c.nome)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all"
                  style={{ borderColor: checked ? ACCENT : "#e8e3dc", backgroundColor: checked ? "#fdf6f1" : "white", color: checked ? ACCENT : "#6b6b6b" }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: ACCENT }}>
                    {(c.nome || "?")[0].toUpperCase()}
                  </div>
                  <span>{c.nome} {c.cognome}</span>
                  {c.ruolo && <span className="text-xs opacity-60">· {c.ruolo}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab 4: Info private ───────────────────────────────────────────────────────
function TabInfoPrivate({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: "#eff6ff", color: "#1d4ed8" }}>
        <span>ℹ️</span>
        <span>Informazioni visibili solo agli ospiti con prenotazione attiva.</span>
      </div>

      <Field label="Istruzioni Check-in">
        <Textarea
          value={form.istruzioni_checkin}
          onChange={v => set("istruzioni_checkin", v)}
          rows={4}
          placeholder="Inserisci istruzioni dettagliate per il check-in"
        />
      </Field>

      <Field label="Istruzioni Check-out">
        <Textarea
          value={form.istruzioni_checkout}
          onChange={v => set("istruzioni_checkout", v)}
          rows={4}
          placeholder="Inserisci istruzioni dettagliate per il check-out"
        />
      </Field>

      <Field label="Regole della casa">
        <Textarea
          value={form.regole_casa}
          onChange={v => set("regole_casa", v)}
          rows={4}
          placeholder="Inserisci regole della casa e restrizioni"
        />
      </Field>

      <Field label="Dotazioni ed elettrodomestici">
        <Textarea
          value={form.dotazioni}
          onChange={v => set("dotazioni", v)}
          rows={4}
          placeholder="Inserisci elenco dotazioni ed elettrodomestici disponibili"
        />
      </Field>

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Informazioni personalizzate</h3>
        <button
          type="button"
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
          style={{ backgroundColor: ACCENT }}
        >
          <Plus className="w-3 h-3" /> Aggiungi
        </button>
      </div>
    </div>
  );
}

// ── Tab 5: Da sapere ──────────────────────────────────────────────────────────
function TabDaSapere({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: "#f0fdf4", color: "#166534" }}>
        <span>🌍</span>
        <span>Consigli e informazioni per esplorare la zona.</span>
      </div>

      <Field label="Cose da fare">
        <Textarea
          value={form.cose_da_fare}
          onChange={v => set("cose_da_fare", v)}
          rows={4}
          placeholder="Inserisci attività e attrazioni turistiche nei dintorni"
        />
      </Field>

      <Field label="Dove mangiare">
        <Textarea
          value={form.dove_mangiare}
          onChange={v => set("dove_mangiare", v)}
          rows={4}
          placeholder="Inserisci ristoranti, bar e locali consigliati"
        />
      </Field>

      <Field label="Trasporti">
        <Textarea
          value={form.trasporti_zona}
          onChange={v => set("trasporti_zona", v)}
          rows={4}
          placeholder="Inserisci informazioni sui trasporti pubblici e collegamenti"
        />
      </Field>

      <Field label="Parcheggio">
        <Textarea
          value={form.parcheggio_note}
          onChange={v => set("parcheggio_note", v)}
          rows={4}
          placeholder="Inserisci dettagli sulle opzioni di parcheggio disponibili"
        />
      </Field>

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Informazioni personalizzate</h3>
        <button
          type="button"
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
          style={{ backgroundColor: ACCENT }}
        >
          <Plus className="w-3 h-3" /> Aggiungi
        </button>
      </div>
    </div>
  );
}

// ── Tab 6: Posizione ──────────────────────────────────────────────────────────
function TabPosizione({ form, set }: { form: FormData; set: (k: keyof FormData, v: any) => void }) {
  const mapsUrl = form.latitudine && form.longitudine
    ? `https://www.google.com/maps/search/?api=1&query=${form.latitudine},${form.longitudine}`
    : "https://maps.google.com";

  return (
    <div className="space-y-6">
      <Field label="Descrizione posizione">
        <Textarea
          value={form.descrizione_posizione}
          onChange={v => set("descrizione_posizione", v)}
          rows={3}
          placeholder="Inserisci descrizione dettagliata della posizione e dintorni"
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Latitudine">
          <Input value={form.latitudine} onChange={v => set("latitudine", v)} placeholder="Inserisci coordinata latitudine" />
        </Field>
        <Field label="Longitudine">
          <Input value={form.longitudine} onChange={v => set("longitudine", v)} placeholder="Inserisci coordinata longitudine" />
        </Field>
      </div>

      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full border border-border rounded-xl py-3 text-sm text-foreground hover:bg-muted/40 transition-colors"
      >
        <MapPin className="w-4 h-4" /> Cerca su Google Maps
      </a>

      <div>
        <Field label="Incorpora mappa Google Maps">
          <p className="text-xs text-muted-foreground mb-2">Copia l'iframe da Google Maps → Condividi → Incorpora una mappa</p>
          <Textarea
            value={form.mappa_embed}
            onChange={v => set("mappa_embed", v)}
            rows={4}
            placeholder="Inserisci codice iframe per incorporare mappa Google Maps"
          />
        </Field>
        {form.mappa_embed && form.mappa_embed.includes("<iframe") && (
          <div
            className="mt-3 rounded-xl overflow-hidden border border-border h-48"
            dangerouslySetInnerHTML={{ __html: form.mappa_embed }}
          />
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function StrutturePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = id === "new";

  const { data: struttura, isLoading } = useGetStruttura(
    id ?? "",
    { query: { enabled: !isNew && !!id, queryKey: ['struttura', id] } }
  );

  const create = useCreateStruttura();
  const update = useUpdateStruttura();

  const [form, setForm] = useState<FormData>(empty);
  const [tab, setTab] = useState(0);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (struttura && !isNew) {
      const s = struttura as any;
      setForm({
        nome: s.nome ?? "",
        slogan: s.slogan ?? "",
        tipo: s.tipo ?? "Appartamento",
        paese: s.paese ?? "Italia",
        visibilita: s.visibilita ?? "Attiva",
        regione: s.regione ?? "",
        citta: s.citta ?? "",
        indirizzo: s.indirizzo ?? "",
        piano: s.piano ?? "",
        num_camere: s.num_camere ?? 1,
        ospiti_max: s.ospiti_max ?? 2,
        superficie: s.superficie ?? 0,
        parcheggio: s.parcheggio ?? "Nessuno",
        link_prenotazione: s.link_prenotazione ?? "",
        descrizione: s.descrizione ?? "",
        telefono: s.telefono ?? "",
        cin: s.cin ?? "",
        pet_friendly: s.pet_friendly ?? false,
        host_assegnato: s.host_assegnato ?? "",
        host_id: s.host_id ?? "",
        stato: s.stato ?? "Attiva",
        foto_principale: s.foto_principale ?? "",
        logo: s.logo ?? "",
        servizi: s.servizi ?? "[]",
        istruzioni_checkin: s.istruzioni_checkin ?? "",
        istruzioni_checkout: s.istruzioni_checkout ?? "",
        regole_casa: s.regole_casa ?? "",
        dotazioni: s.dotazioni ?? "",
        info_private_extra: s.info_private_extra ?? "[]",
        cose_da_fare: s.cose_da_fare ?? "",
        dove_mangiare: s.dove_mangiare ?? "",
        trasporti_zona: s.trasporti_zona ?? "",
        parcheggio_note: s.parcheggio_note ?? "",
        da_sapere_extra: s.da_sapere_extra ?? "[]",
        descrizione_posizione: s.descrizione_posizione ?? "",
        latitudine: s.latitudine ?? "",
        longitudine: s.longitudine ?? "",
        mappa_embed: s.mappa_embed ?? "",
      });
    }
  }, [struttura, isNew]);

  const set = (k: keyof FormData, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.nome) return;
    setSaveStatus("saving");
    setSaveError(null);
    const onSuccess = async (saved?: any) => {
      const struttura_id = isNew ? saved?.id : id;
      if (struttura_id) {
        const prevHostId = (struttura as any)?.host_id ?? null;
        try {
          await syncHostStruttura({
            struttura_id,
            new_host_id: form.host_id || null,
            old_host_id: prevHostId,
          });
        } catch (_) {}
      }
      qc.invalidateQueries({ queryKey: ["strutture"] });
      qc.invalidateQueries({ queryKey: ["host-profiles"] });
      if (!isNew) qc.invalidateQueries({ queryKey: ["struttura", id] });
      setSaveStatus("saved");
      if (isNew && saved?.id) {
        navigate(`/strutture/${saved.id}`, { replace: true });
      } else {
        setTimeout(() => setSaveStatus("idle"), 2500);
      }
    };
    const onError = (err: any) => {
      setSaveStatus("error");
      setSaveError(err?.message ?? "Errore durante il salvataggio. Riprova.");
    };
    if (isNew) {
      create.mutate(form as any, { onSuccess, onError });
    } else {
      update.mutate({ id: id!, data: form as any }, { onSuccess, onError });
    }
  };

  if (isLoading && !isNew) {
    return <div className="text-sm text-muted-foreground p-8">Caricamento...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate("/strutture/lista")}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors flex-shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground">
            {isNew ? "Nuova struttura" : "Modifica struttura"}
          </h1>
          {!isNew && form.nome && (
            <p className="text-sm text-muted-foreground mt-0.5">{form.nome}</p>
          )}
        </div>
      </div>

      {/* Tabs card */}
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
        {/* Mobile: select dropdown */}
        <div className="md:hidden px-4 pt-4 pb-3">
          <div className="relative">
            <select
              value={tab}
              onChange={e => setTab(Number(e.target.value))}
              className="w-full appearance-none rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none pr-10"
              style={{
                background: ACCENT,
                color: "#fff",
                border: "none",
              }}
            >
              {TABS.map((t, i) => (
                <option key={t} value={i} style={{ background: "#fff", color: "#222" }}>{t}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </div>
        </div>

        {/* Desktop: tab bar */}
        <div className="hidden md:flex overflow-x-auto border-b border-border">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              className="px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0"
              style={{
                color: tab === i ? ACCENT : "#6b6b6b",
                borderBottom: tab === i ? `2px solid ${ACCENT}` : "2px solid transparent",
              }}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="p-4 md:p-6">
          {tab === 0 && <TabStruttura form={form} set={set} />}
          {tab === 1 && <TabFoto form={form} set={set} />}
          {tab === 2 && <TabServizi form={form} set={set} />}
          {tab === 3 && <TabTeam form={form} set={set} />}
          {tab === 4 && <TabInfoPrivate form={form} set={set} />}
          {tab === 5 && <TabDaSapere form={form} set={set} />}
          {tab === 6 && <TabPosizione form={form} set={set} />}
        </div>
      </div>

      {/* Save button + feedback */}
      <div className="mt-4 space-y-3">
        {saveStatus === "saved" && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            Modifiche salvate con successo
          </div>
        )}
        {saveStatus === "error" && saveError && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{saveError}</span>
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saveStatus === "saving"}
          className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-semibold text-sm transition-opacity disabled:opacity-50"
        >
          {saveStatus === "saving" ? "Salvataggio in corso..." : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}
